/**
 * ShareModal — owner-only. Invite collaborators by email with a permission
 * level, copy the invite link, change roles, and revoke access.
 */
import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { X, Copy, Check, Trash2, Mail, Eye, MessageSquare, PenLine } from "lucide-react";

const ROLES = [
  { value: "viewer", label: "View only", hint: "Can read the script. Free forever.", Icon: Eye },
  { value: "feedback", label: "Feedback", hint: "Can read & leave notes. Free forever.", Icon: MessageSquare },
  { value: "cowriter", label: "Co-writer", hint: "Full edit access. Needs own subscription.", Icon: PenLine },
];

export function ShareModal({ scriptId, scriptTitle, onClose }) {
  const confirm = useConfirm();
  const [shares, setShares] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/scripts/${scriptId}/shares`); setShares(data.shares || []); }
    catch {}
  }, [scriptId]);
  useEffect(() => { load(); }, [load]);

  // Build the invite link from the current origin so it always matches the
  // domain the user is actually on (preview or production) — no server-side
  // URL inference, no hardcoded hosts.
  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const invite = async (e) => {
    e?.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/shares`, { email: clean, role, origin_url: window.location.origin });
      setEmail("");
      await load();
      copyLink(data.share.id, inviteUrl(data.share.token));
      toast.success("Invite created — link copied to clipboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't create the invite");
    } finally { setBusy(false); }
  };

  const copyLink = (id, link) => {
    try {
      navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {}
  };

  const changeRole = async (share, newRole) => {
    try {
      await api.put(`/scripts/${scriptId}/shares/${share.id}`, { role: newRole });
      setShares((prev) => prev.map((s) => (s.id === share.id ? { ...s, role: newRole } : s)));
    } catch { toast.error("Couldn't update permission"); }
  };

  const revoke = async (share) => {
    const ok = await confirm({
      title: `Remove ${share.invited_email}?`,
      description: "They'll immediately lose access to this script.",
      confirmLabel: "Remove access",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/scripts/${scriptId}/shares/${share.id}`);
      setShares((prev) => prev.filter((s) => s.id !== share.id));
    } catch { toast.error("Couldn't remove access"); }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[110] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="share-modal-overlay"
      >
        <div className="absolute inset-0 bg-[#1c1c1c]/45 backdrop-blur-[3px]" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-[520px] bg-[var(--paper)] border border-[var(--line)] rounded-xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          data-testid="share-modal"
        >
          <div className="flex items-center justify-between px-6 h-14 border-b border-[var(--line)]">
            <div className="flex items-center gap-2 min-w-0">
              <Mail size={16} className="text-[var(--brand)] shrink-0" />
              <h2 className="font-display text-lg text-[var(--ink-primary)] truncate">Share &ldquo;{scriptTitle || "Untitled"}&rdquo;</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="share-modal-close">
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>

          <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
            <form onSubmit={invite} className="space-y-3" data-testid="share-invite-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                data-testid="share-email-input"
                className="w-full px-3 py-2.5 bg-white border border-[var(--line)] rounded-md text-sm focus:border-[var(--brand)] outline-none"
              />
              <div className="grid grid-cols-3 gap-2" data-testid="share-role-picker">
                {ROLES.map(({ value, label, hint, Icon }) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setRole(value)}
                    data-testid={`share-role-${value}`}
                    className={`text-left p-3 rounded-lg border transition-colors ${role === value ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--line)] hover:bg-[var(--warm-accent)]"}`}
                  >
                    <Icon size={15} className={role === value ? "text-[var(--brand)]" : "text-[var(--ink-muted)]"} />
                    <div className="text-sm font-medium text-[var(--ink-primary)] mt-1.5">{label}</div>
                    <div className="text-[10px] text-[var(--ink-muted)] mt-0.5 leading-snug">{hint}</div>
                  </button>
                ))}
              </div>
              <button type="submit" disabled={busy} className="btn-brand w-full py-2.5 text-sm disabled:opacity-60" data-testid="share-invite-btn">
                {busy ? "Creating invite…" : "Create invite link"}
              </button>
            </form>

            <div className="border-t border-[var(--line)] pt-4">
              <div className="text-[11px] uppercase tracking-wider text-[var(--ink-muted)] mb-2">People with access</div>
              {shares.length === 0 ? (
                <p className="text-sm text-[var(--ink-muted)] py-3" data-testid="share-empty">No one yet. Invite a collaborator above.</p>
              ) : (
                <ul className="space-y-2" data-testid="share-list">
                  {shares.map((s) => (
                    <li key={s.id} className="flex items-center gap-2 bg-white border border-[var(--line)] rounded-lg px-3 py-2" data-testid={`share-row-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[var(--ink-primary)] truncate">{s.invited_email}</div>
                        <div className="text-[11px] text-[var(--ink-muted)]">
                          {s.status === "pending" ? "Invite sent · not yet accepted" : "Active"}
                        </div>
                      </div>
                      <select
                        value={s.role}
                        onChange={(e) => changeRole(s, e.target.value)}
                        data-testid={`share-role-select-${s.id}`}
                        className="text-xs bg-[var(--warm-accent)] border border-[var(--line)] rounded px-2 py-1.5 outline-none"
                      >
                        <option value="viewer">View only</option>
                        <option value="feedback">Feedback</option>
                        <option value="cowriter">Co-writer</option>
                      </select>
                      <button onClick={() => copyLink(s.id, inviteUrl(s.token))} title="Copy invite link" className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid={`share-copy-${s.id}`}>
                        {copiedId === s.id ? <Check size={15} className="text-[var(--ok,#3a7d44)]" /> : <Copy size={15} />}
                      </button>
                      <button onClick={() => revoke(s)} title="Remove access" className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-muted)] hover:text-[var(--err)]" data-testid={`share-revoke-${s.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
